import ActionTypes from 'redux/actions/ActionTypes';
import { put, call, select } from 'redux-saga/effects';
import WarrantyService from 'services/WarrantyService';
import {
  getUserProfileAndSettings,
  getWarranty,
  getUser,
  getWarrantyServiceItems,
  getAccountProfile,
  getLotAssociations,
} from 'redux/sagas/selectors';
import Utils from 'utilities/Utils';
import { assignCategories } from 'redux/sagas/sagaHelpers/warrantyHelpers';
import { _sendRequest } from 'redux/sagas/sagaHelpers/generalHelpers';
import { buildErrorObject } from 'redux/sagas/sagaHelpers/userHelpers';
import LogService from 'services/LogService';

async function fetchCategories(lotId) {
  try {
    return await _sendRequest(
      { lotId, sharedApi: true },
      WarrantyService.getCategories
    );
  } catch (error) {
    return buildErrorObject(error);
  }
}

async function log(data = '') {
  try {
    return await _sendRequest(data, LogService.logInfo);
  } catch (error) {
    return buildErrorObject(error);
  }
}

async function getServiceRequestId(possibleRequestIdAndLot) {
  // just forcing it to be an int
  try {
    const res = await _sendRequest(
      possibleRequestIdAndLot,
      WarrantyService.getServiceRequestId,
      true
    );
    return res;
  } catch (error) {
    return buildErrorObject(error);
  }
}

async function fetchCategoryData(data) {
  try {
    return await _sendRequest(data, WarrantyService.getCategoryData);
  } catch (error) {
    return buildErrorObject(error);
  }
}

async function submitClaim(data) {
  try {
    const r = await _sendRequest(
      data,
      WarrantyService.submitClaim,
      false,
      true
    );
    return r;
  } catch (error) {
    return buildErrorObject(error);
  }
}

export function* handleSubmitWarrantyClaimSaga(index) {
  const warrantyState = yield select(getWarranty);
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  const { activeLotId: lotId } = userProfileAndSettings;
  const { claimsData } = warrantyState.warrantyClaims;
  const data = claimsData[index];
  const {
    serviceRequestId,
    categoryId,
    categoryData,
    selectedIssue,
    modelNumber,
    selectedSubCategory,
    images,
  } = data;
  const singleSelectedCategoryData = categoryData.filter(
    (cat) =>
      cat.categoryId === categoryId &&
      (cat.description === selectedSubCategory ||
        cat.displayName === selectedSubCategory)
  )[0];

  const issue = singleSelectedCategoryData.issues.filter(
    (issue) =>
      issue.name === selectedIssue || issue.displayName == selectedIssue
  )[0]; // && issue.subCategoryDescription === selectedSubCategory;
  const { subCategoryId, id } = issue || {
    subCategoryId: singleSelectedCategoryData.id,
    id: null,
  };

  const attachments = images.map(({ imageBase64 }, imageIndex) => {
    if (
      imageBase64 === null ||
      imageBase64 === undefined ||
      imageBase64 === ''
    ) {
      return null;
    }
    return {
      attachment: imageBase64,
      attachmentFilename: `${`claim-${index}-image-${imageIndex}`.substring(
        0,
        50
      )}.jpg`,
    };
  });
  const dataToSend = {
    serviceRequestId,
    categoryId,
    subCategoryId,
    lotId,
    issueId: id,
    modelNumber,
    description: data.comments,
    attachments: attachments.filter((item) => item !== null),
    subCategoryName: selectedSubCategory,
  };
  if (
    serviceRequestId === null ||
    serviceRequestId === undefined ||
    serviceRequestId === 0 ||
    serviceRequestId === '0' ||
    serviceRequestId.toString().length <= 1
  ) {
    console.error('Service Request ID is invalid before submitting items');
    yield call(
      log,
      `Service Request ID is invalid before submitting items: ${JSON.stringify(
        dataToSend
      )}`
    );
  } else {
    console.log('Service Request ID is valid, proceeding to submit items');
  }
  const submitClaimCompleteResponse = yield call(submitClaim, dataToSend);
  return submitClaimCompleteResponse;
}

export function* categoriesRequestSaga() {
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  const { activeLotId: lotId } = userProfileAndSettings;
  const categoriesResponse = yield call(fetchCategories, lotId);
  if (Array.isArray(categoriesResponse)) {
    yield call(
      log,
      `${userProfileAndSettings.activeLotId}-get-categories-success`
    );
    yield put({
      type: ActionTypes.GetCategoriesSuccess,
      payload: assignCategories(categoriesResponse),
    });
  } else {
    yield call(
      log,
      `${userProfileAndSettings.activeLotId}-get-categories-failure`
    );
    yield put({
      type: ActionTypes.GetCategoriesError,
    });
  }
}

export function* getServiceRequestIdSaga(action) {
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  const accountProfile = yield select(getAccountProfile);
  const lots = yield select(getLotAssociations);
  let lotSummary = {
    address: {},
  };
  if (lots && Array.isArray(lots)) {
    lotSummary = lots?.filter(
      ({ lotId }) => lotId === userProfileAndSettings.activeLotId
    )[0].lotSummary;
  }
  const { address } = lotSummary;
  const { firstName, lastName, email, phoneNumber } = accountProfile;

  const dataToSend = {
    //requestId: action.payload,
    lotId: parseInt(userProfileAndSettings.activeLotId),
    firstName,
    lastName,
    email,
    phoneNumber,
    address,
    source: 'Buyer Portal',
  };

  const serviceRequestResponse = yield call(getServiceRequestId, dataToSend);

  if (
    !serviceRequestResponse ||
    serviceRequestResponse?.errors ||
    serviceRequestResponse.serviceRequestId === null ||
    serviceRequestResponse.serviceRequestId === undefined
  ) {
    yield call(
      log,
      `${userProfileAndSettings.activeLotId}-get-service-request-id-failure`
    );
    yield call(
      log,
      `${
        userProfileAndSettings.activeLotId
      }-get-service-request-id-errors-${JSON.stringify(
        serviceRequestResponse?.errors
      )}`
    );
    yield call(
      log,
      `${
        userProfileAndSettings.activeLotId
      }-get-service-request-id-data-${JSON.stringify(dataToSend)}`
    );
    const serviceItems = yield select(getWarrantyServiceItems);
    yield call(
      log,
      `${
        userProfileAndSettings.activeLotId
      }-get-service-request-id-items-${JSON.stringify(serviceItems)}`
    );
    yield call(
      log,
      `${userProfileAndSettings.activeLotId}-get-service-request-id-ua-${window.navigator.userAgent}`
    );

    yield put({
      type: ActionTypes.ApiFatalError,
      payload: {
        link: '/dashboard',
        text: 'Back To Dashboard',
      },
    });
  } else {
    yield call(
      log,
      `${userProfileAndSettings.activeLotId}-get-service-request-id-success-${serviceRequestResponse.serviceRequestId}`
    );
    yield put({
      type: ActionTypes.GetServiceRequestIdSuccess,
      payload: serviceRequestResponse.serviceRequestId,
    });
  }
}

async function completeServiceRequest(possibleRequestIdAndLotId) {
  // const { lotId, serviceRequestId } = possibleRequestIdAndLotId;
  try {
    return await _sendRequest(
      possibleRequestIdAndLotId,
      WarrantyService.completeServiceRequest,
      true
    );
  } catch (error) {
    return buildErrorObject(error);
  }
}

export function* completeServiceRequestSaga() {
  const warrantyState = yield select(getWarranty);
  const { requestId } = warrantyState.warrantyClaims;
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  const { activeLotId: lotId } = userProfileAndSettings;
  if (requestId === 'requested') {
    // extra safeguard
    yield call(log, `${lotId}-complete-request-requested-error`);

    yield put({
      type: ActionTypes.ApiFatalError,
      payload: {
        link: '/dashboard',
        text: 'Back To Dashboard',
      },
    });
  } else {
    const dataToSend = {
      serviceRequestId: requestId,
      lotId,
    };
    const res = yield call(completeServiceRequest, dataToSend);
    if (res.errors) {
      yield call(
        log,
        `${userProfileAndSettings.activeLotId}-complete-service-request-failure-${requestId}`
      );

      yield put({
        type: ActionTypes.ApiFatalError,
        payload: {
          link: '/dashboard',
          text: 'Back To Dashboard',
        },
      });
    } else {
      yield call(
        log,
        `${userProfileAndSettings.activeLotId}-complete-service-request-success-${requestId}`
      );
      yield put({
        type: ActionTypes.ServiceRequestCompleted,
      });
    }
  }
}

export function* categoryDataRequestSaga(action) {
  const data = {
    categoryId: action.payload.categoryId,
    categoryName: action.payload.categoryName,
    categoryTooltip: action.payload.categoryTooltip,
  };
  const serviceItems = yield select(getWarrantyServiceItems);
  yield put({
    type: ActionTypes.AddClaim,
    payload: {
      categoryId: data.categoryId,
      categoryName: data.categoryName,
      categoryTooltip: data.categoryTooltip,
      categoryData: [],
      selectedSubCategory: '',
      selectedIssue: '',
      modelNumber: '',
      comments: '',
      images: [{}, {}, {}, {}, {}, {}],
      serviceItems,
    },
  });
  const categoryDataResponse = yield call(fetchCategoryData, data);
  if (categoryDataResponse) {
    yield put({
      type: ActionTypes.UpdateClaimDetails,
      payload: {
        type: 'categoryData',
        value: categoryDataResponse,
        id: data.categoryId,
      },
    });
  } else {
    yield put({
      type: ActionTypes.GetCategoryDataError,
    });
  }
}

export function* saveClaimSaga(action) {
  const user = yield select(getUser);
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  const warrantyState = yield select(getWarranty);
  const obj = {
    claimsData: warrantyState.warrantyClaims.claimsData,
    activeCategories: warrantyState.warrantyDetails.activeCategories,
    openClaim:
      action && action.payload && action.payload.openClaim
        ? action.payload.openClaim
        : warrantyState.warrantyDetails.openClaimForm,
    pageStatus:
      action && action.payload && action.payload.page
        ? action.payload.page
        : warrantyState.warrantyDetails.pageStatus,
  };
  try {
    Utils.setLocalStorage(
      Utils.getWarrantyClaimName(
        user.user.sitecoreId,
        userProfileAndSettings.activeLotId
      ),
      JSON.stringify(obj)
    );
    yield put({
      type: ActionTypes.SaveClaimDataToFileSuccess,
    });
  } catch (e) {
    yield put({
      type: ActionTypes.SaveClaimDataToFileError,
    });
  }
}

export function* submitWarrantyClaimsSaga() {
  const warrantyState = yield select(getWarranty);
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  const { claimsData } = warrantyState.warrantyClaims;
  // eslint-disable-next-line no-restricted-syntax
  for (const i in claimsData) {
    if (Utils.isNumeric(i) && claimsData[i].submissionStatus !== 'submitted') {
      const t = performance.now();
      yield put({
        type: ActionTypes.ServiceItemSubmitRequest,
        payload: {
          idOrIndex: parseInt(i, 10),
          time: t,
        },
      });
      const submitClaimResponse = yield call(handleSubmitWarrantyClaimSaga, i);
      if (
        submitClaimResponse &&
        submitClaimResponse.serviceRequestId &&
        submitClaimResponse.serviceItemId
      ) {
        yield put({
          type: ActionTypes.ServiceItemSubmitSuccess,
          payload: {
            idOrIndex: parseInt(i, 10),
            time: performance.now(),
          },
        });
        yield call(
          log,
          `${userProfileAndSettings.activeLotId}-submit-single-item-success-serviceRequestId:${submitClaimResponse.serviceRequestId}`
        );
      } else {
        yield call(
          log,
          `${
            userProfileAndSettings.activeLotId
          }-submit-single-item-failure-${JSON.stringify(i)}`
        );
        yield put({
          type: ActionTypes.ApiFatalError,
          // payload: {
          //     link: '/dashboard',
          //     text: 'Back To Dashboard',
          // },
        });
      }
    }
  }
  yield put({
    type: ActionTypes.ServiceItemBatchSubmitSuccess,
  });
}

export function* removeClaimSaga(action) {
  const { categoryId } = action.payload;
  const warrantyState = yield select(getWarranty);
  const { claimsData } = warrantyState.warrantyClaims;

  const claimToRemove = claimsData.find((claim) => {
    return claim.categoryId === categoryId;
  });

  yield put({
    type: ActionTypes.RemoveClaim,
    payload: claimToRemove,
  });
}

export function* removeExistingClaimsDraftSaga() {
  const user = yield select(getUser);
  const userProfileAndSettings = yield select(getUserProfileAndSettings);
  Utils.removeLocalStorage(
    Utils.getWarrantyClaimName(
      user.user.sitecoreId,
      userProfileAndSettings.activeLotId
    )
  );
  yield put({
    type: ActionTypes.HideClaimDraftModal,
  });
}
